import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PULSE_COMMAND_TOGGLE_KEY } from '../config/pulseCommand';
import {
  PULSE_COMMAND_DEBOUNCE_MS,
  searchPulseCommand
} from '../utils/pulseCommandSearch';

function flattenResults(groups) {
  return (groups || []).flatMap((g) => g.items || []);
}

export function usePulseCommand({
  user,
  navItems = [],
  shortcuts = [],
  getConversations = () => [],
  unreadCount = 0
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const userRef = useRef(user);
  const navItemsRef = useRef(navItems);
  const shortcutsRef = useRef(shortcuts);
  const getConversationsRef = useRef(getConversations);
  const unreadCountRef = useRef(unreadCount);
  const activeIndexRef = useRef(0);
  const navigateRef = useRef(navigate);

  userRef.current = user;
  navItemsRef.current = navItems;
  shortcutsRef.current = shortcuts;
  getConversationsRef.current = getConversations;
  unreadCountRef.current = unreadCount;
  activeIndexRef.current = activeIndex;
  navigateRef.current = navigate;

  const flatItems = useMemo(() => flattenResults(groups), [groups]);
  const flatItemsRef = useRef(flatItems);
  flatItemsRef.current = flatItems;

  const setQueryAndResetIndex = useCallback((next) => {
    setQuery(next);
    setActiveIndex(0);
    activeIndexRef.current = 0;
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    setActiveIndex(0);
    activeIndexRef.current = 0;
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setGroups([]);
    setActiveIndex(0);
    activeIndexRef.current = 0;
    setLoading(false);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const runItem = useCallback(
    (item) => {
      if (!item?.run) return;
      close();
      item.run();
    },
    [close]
  );

  const runActive = useCallback(() => {
    const items = flatItemsRef.current;
    const idx = activeIndexRef.current;
    const item = items[idx];
    if (item) runItem(item);
  }, [runItem]);

  const moveActive = useCallback((delta) => {
    const len = flatItemsRef.current.length;
    if (!len) return;
    setActiveIndex((i) => {
      const next = (i + delta + len) % len;
      activeIndexRef.current = next;
      return next;
    });
  }, []);

  const handlePaletteKeyDown = useCallback(
    (event) => {
      const key = String(event.key || '');

      if (key === 'ArrowDown') {
        event.preventDefault();
        moveActive(1);
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
        return;
      }

      if (key === 'Enter') {
        event.preventDefault();
        runActive();
      }
    },
    [moveActive, runActive]
  );

  useEffect(() => {
    if (!isOpen || !userRef.current) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      try {
        const next = await searchPulseCommand({
          query,
          user: userRef.current,
          navigate: navigateRef.current,
          navItems: navItemsRef.current,
          shortcuts: shortcutsRef.current,
          conversations: getConversationsRef.current(),
          unreadCount: unreadCountRef.current,
          signal: ac.signal
        });
        if (!ac.signal.aborted) {
          setGroups(next);
          const len = flattenResults(next).length;
          setActiveIndex((i) => {
            const clamped = len ? Math.min(i, len - 1) : 0;
            activeIndexRef.current = clamped;
            return clamped;
          });
        }
      } catch (err) {
        if (err?.name !== 'AbortError') setGroups([]);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, query ? PULSE_COMMAND_DEBOUNCE_MS : 0);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOpen, query]);

  useEffect(() => {
    if (!user) return undefined;

    const onKeyDown = (event) => {
      const key = String(event.key || '').toLowerCase();
      const mod = event.metaKey || event.ctrlKey;

      if (mod && key === PULSE_COMMAND_TOGGLE_KEY) {
        event.preventDefault();
        if (isOpen) close();
        else open();
        return;
      }

      if (!isOpen) return;

      if (key === 'escape') {
        event.preventDefault();
        close();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close, isOpen, open, user]);

  return {
    isOpen,
    query,
    setQuery: setQueryAndResetIndex,
    groups,
    flatItems,
    activeIndex,
    setActiveIndex: (idx) => {
      activeIndexRef.current = idx;
      setActiveIndex(idx);
    },
    loading,
    open,
    close,
    runItem,
    runActive,
    moveActive,
    handlePaletteKeyDown
  };
}
